import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Lock, LogOut, RefreshCw, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const ADMIN_TOKEN_KEY = 'admin_reports_token';
const ADMIN_USER_KEY = 'admin_reports_user';

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(Number(value || 0));
const formatDateTimeIST = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const toEpoch = (value) => {
  const ts = Date.parse(String(value || ''));
  return Number.isFinite(ts) ? ts : 0;
};

export default function AdminReports() {
  const [username, setUsername] = useState(() => sessionStorage.getItem(ADMIN_USER_KEY) || 'admin');
  const [password, setPassword] = useState('');
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [days, setDays] = useState('30');
  const [authLoading, setAuthLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const loadReport = async (token = adminToken) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/reports/funnel?days=${encodeURIComponent(days)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to load report');
      }
      setReport(data);
    } catch (err) {
      const message = err.message || 'Failed to load report';
      setError(message);
      if (message.toLowerCase().includes('session') || message.toLowerCase().includes('unauthorized')) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      loadReport(adminToken);
    }
  }, [adminToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Login failed');
      }
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      sessionStorage.setItem(ADMIN_USER_KEY, data?.admin?.username || username);
      setAdminToken(data.token);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_USER_KEY);
    setAdminToken('');
    setReport(null);
    setPassword('');
  };

  const summary = report?.summary || {};
  const sources = report?.source_breakdown || [];
  const dailyRows = [...(report?.daily_funnel || [])].sort((a, b) => toEpoch(b.date) - toEpoch(a.date));
  const leadStatuses = report?.lead_status_snapshot || [];
  const tutorRegistry = report?.tutor_registry || [];
  const notes = report?.notes || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <div>
              <div className="text-xl font-bold text-slate-900">Tricity Tutors</div>
              <div className="text-xs text-slate-500">Admin Reports</div>
            </div>
          </Link>
          {adminToken ? (
            <div className="flex items-center gap-3">
              <Button asChild variant="outline">
                <Link to="/admin/tutors">Tutors Details</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/students">Student Details</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/leads">Lead Details</Link>
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          ) : (
            <Badge variant="outline" className="text-slate-600">Admin Only</Badge>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {!adminToken ? (
          <Card className="max-w-lg mx-auto">
            <CardHeader>
              <CardTitle>Admin Login</CardTitle>
              <CardDescription>Sign in to access internal reporting.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="admin-username">Username</Label>
                  <Input
                    id="admin-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="mt-2"
                    autoComplete="username"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2"
                    autoComplete="current-password"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <Button type="submit" disabled={authLoading || !username.trim() || !password} className="w-full">
                  <Lock className="w-4 h-4 mr-2" />
                  {authLoading ? 'Signing In' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Report Controls</CardTitle>
                <CardDescription>Refresh the internal funnel report for the selected window.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-[140px_160px] gap-4 items-end">
                <div>
                  <Label htmlFor="days">Days</Label>
                  <Input
                    id="days"
                    type="number"
                    min="1"
                    max="365"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <Button onClick={() => loadReport()} disabled={loading} className="h-10">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Loading' : 'Refresh'}
                </Button>
              </CardContent>
            </Card>

            {error && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6 flex items-start gap-3 text-red-800">
                  <ShieldAlert className="w-5 h-5 mt-0.5" />
                  <div>{error}</div>
                </CardContent>
              </Card>
            )}

            {report && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Users</div><div className="text-3xl font-bold">{formatNumber(summary.total_users)}</div><div className="text-xs text-slate-500 mt-2">Tutors {formatNumber(summary.total_tutors)} | Students {formatNumber(summary.total_students)}</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Signups ({report.days}d)</div><div className="text-3xl font-bold">{formatNumber((summary.new_tutor_signups || 0) + (summary.new_student_signups || 0))}</div><div className="text-xs text-slate-500 mt-2">Tutor {formatNumber(summary.new_tutor_signups)} | Student {formatNumber(summary.new_student_signups)}</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Requirements ({report.days}d)</div><div className="text-3xl font-bold">{formatNumber(summary.requirements_posted)}</div><div className="text-xs text-slate-500 mt-2">Active now {formatNumber(summary.active_requirements)}</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Revenue ({report.days}d)</div><div className="text-3xl font-bold">₹{formatNumber(summary.revenue_inr)}</div><div className="text-xs text-slate-500 mt-2">Purchases {formatNumber(summary.purchases)}</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Student Contact Unlocks</div><div className="text-3xl font-bold">{formatNumber(summary.contact_unlocks)}</div><div className="text-xs text-slate-500 mt-2">Last {report.days} days</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Tutor Lead Unlocks</div><div className="text-3xl font-bold">{formatNumber(summary.requirement_unlocks)}</div><div className="text-xs text-slate-500 mt-2">Last {report.days} days</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Tutor Profile Views</div><div className="text-3xl font-bold">{formatNumber(summary.tutor_profile_views_total)}</div><div className="text-xs text-slate-500 mt-2">Lifetime total</div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="text-sm text-slate-500">Tracked Analytics Events</div><div className="text-3xl font-bold">{formatNumber(summary.analytics_events_tracked)}</div><div className="text-xs text-slate-500 mt-2">Stored internally</div></CardContent></Card>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Daily Funnel</CardTitle>
                      <CardDescription>Business metrics by day for the selected window.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Signups</TableHead>
                            <TableHead>Reqs</TableHead>
                            <TableHead>Contacts</TableHead>
                            <TableHead>Lead Unlocks</TableHead>
                            <TableHead>Purchases</TableHead>
                            <TableHead>Revenue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyRows.map((row) => (
                            <TableRow key={row.date}>
                              <TableCell>{row.date}</TableCell>
                              <TableCell>{formatNumber((row.tutor_signups || 0) + (row.student_signups || 0))}</TableCell>
                              <TableCell>{formatNumber(row.requirements_posted)}</TableCell>
                              <TableCell>{formatNumber(row.contact_unlocks)}</TableCell>
                              <TableCell>{formatNumber(row.requirement_unlocks)}</TableCell>
                              <TableCell>{formatNumber(row.purchases)}</TableCell>
                              <TableCell>₹{formatNumber(row.revenue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Lead Status Snapshot</CardTitle>
                      <CardDescription>Current tutor pipeline state across unlocked leads.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {leadStatuses.map((item) => (
                        <div key={item.status} className="flex items-center justify-between border rounded-lg px-4 py-3">
                          <span className="text-slate-700">{item.status}</span>
                          <span className="font-semibold">{formatNumber(item.count)}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Source Breakdown</CardTitle>
                    <CardDescription>Source and campaign attribution from stored analytics events.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {sources.length === 0 ? (
                      <div className="text-sm text-slate-500">No source-attributed analytics events stored yet.</div>
                    ) : sources.map((source) => (
                      <div key={source.source} className="border rounded-xl p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                          <div>
                            <div className="font-semibold text-slate-900">{source.source}</div>
                            <div className="text-xs text-slate-500">Registrations {formatNumber(source.registrations)} | Leads {formatNumber(source.leads)} | Contacts {formatNumber(source.contacts)} | Purchases {formatNumber(source.purchases)}</div>
                          </div>
                          <Badge variant="outline">{source.top_campaigns.length} campaign rows</Badge>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Campaign</TableHead>
                              <TableHead>Registrations</TableHead>
                              <TableHead>Leads</TableHead>
                              <TableHead>Contacts</TableHead>
                              <TableHead>Purchases</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {source.top_campaigns.map((campaign) => (
                              <TableRow key={`${source.source}-${campaign.campaign}`}>
                                <TableCell>{campaign.campaign}</TableCell>
                                <TableCell>{formatNumber(campaign.registrations)}</TableCell>
                                <TableCell>{formatNumber(campaign.leads)}</TableCell>
                                <TableCell>{formatNumber(campaign.contacts)}</TableCell>
                                <TableCell>{formatNumber(campaign.purchases)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {notes.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Notes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm text-slate-600">
                      {notes.map((note) => (
                        <div key={note}>- {note}</div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
